import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminGetSettings, adminUpdateSettings } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { buildEmbed } from '../../lib/videoEmbed';

const PLATFORMS = ['youtube', 'facebook', 'telegram', 'mp4'];

export default function AdminVideosTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try { setS(await adminGetSettings()); } catch { toast.error('Failed to load'); }
    })();
  }, []);

  function updateVideo(key, field, value) {
    setS(prev => ({
      ...prev,
      videos: prev.videos.map(v => v.key === key ? { ...v, [field]: value } : v),
    }));
  }

  async function save() {
    setSaving(true);
    try {
      await adminUpdateSettings(s);
      toast.success('Videos updated.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (!s) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-5 max-w-4xl">
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-base">Country Videos</CardTitle>
          <p className="text-xs text-muted-foreground">Configure the 3 reviews shown on the landing page. Pick the correct platform so the right embed loads.</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {s.videos.map(v => {
            const embed = buildEmbed(v.platform, v.url);
            return (
              <div key={v.key} className="border border-border rounded-xl p-4 space-y-3 bg-[hsl(240_6%_7%)]" data-testid={`admin-video-row-${v.key}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{v.flag}</span>
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{v.key.toUpperCase()}</div>
                      <div className="font-semibold">{v.title} · {v.language}</div>
                    </div>
                  </div>
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Embed: {embed.kind}</span>
                </div>
                <div className="grid gap-3 md:grid-cols-[180px_1fr_1fr]">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Platform</Label>
                    <Select value={v.platform} onValueChange={(val) => updateVideo(v.key, 'platform', val)}>
                      <SelectTrigger data-testid={`admin-video-platform-${v.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PLATFORMS.map(p => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Language label</Label>
                    <Input value={v.language} onChange={e => updateVideo(v.key, 'language', e.target.value)} data-testid={`admin-video-language-${v.key}`} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Video URL</Label>
                    <Input value={v.url} onChange={e => updateVideo(v.key, 'url', e.target.value)} data-testid={`admin-video-url-${v.key}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="cta-sheen border-0 text-white font-semibold h-11 px-6" data-testid="admin-videos-save-button">
          {saving ? 'Saving…' : 'Save videos'}
        </Button>
      </div>
    </div>
  );
}
